import {
    createRootRoute,
    createRoute,
    createRouter,
    Outlet,
    Navigate,
    useNavigate,
} from '@tanstack/react-router'
import { AppShell } from '@/components/AppShell'
import { ErrorScreen, NotFoundScreen } from '@/components/ErrorScreen'
import HomePage from '@/platform/routes/HomePage'
import FormRunPage from '@/platform/routes/FormRunPage'
import SubmissionPage from '@/platform/routes/SubmissionPage'
import SubmissionsListPage from '@/platform/routes/SubmissionsListPage'
import InboxPage from '@/platform/routes/InboxPage'
import FormsAdminPage from '@/platform/admin/FormsAdminPage'
import FormBuilderPage from '@/platform/admin/FormBuilderPage'
import WorkflowsAdminPage from '@/platform/admin/WorkflowsAdminPage'
import DashboardsPage from '@/platform/dashboards/DashboardsPage'
import DashboardPage from '@/platform/dashboards/DashboardPage'
import { useUserRole } from '@/hooks/useUserRole'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'

const rootRoute = createRootRoute({
    component: () => (
        <AppShell>
            <Outlet />
        </AppShell>
    ),
})

const homeRoute = createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomePage })

// ---- generic forms platform ----
const formsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forms', component: () => <Navigate to="/" replace /> })
const submissionsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/submissions', component: SubmissionsListPage })
const submissionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/submissions/$id',
    component: function Submission() {
        const { id } = submissionRoute.useParams()
        return <SubmissionPage key={id} submissionId={id} />
    },
})
const submissionEditRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/submissions/$id/edit',
    component: function SubmissionEdit() {
        const { id } = submissionEditRoute.useParams()
        return <FormRunPage key={id} submissionId={id} />
    },
})
const inboxRoute = createRoute({ getParentRoute: () => rootRoute, path: '/inbox', component: InboxPage })

function ManagerOnly({ children }: { children: React.ReactNode }) {
    const navigate = useNavigate()
    const { isRiskManager, loading } = useUserRole()
    if (loading) return <p className="py-16 text-center text-muted-foreground">Loading…</p>
    if (!isRiskManager) {
        return (
            <Card className="mx-auto mt-16 max-w-md p-8 text-center">
                <h2 className="text-lg font-semibold text-foreground">Access denied</h2>
                <p className="mt-2 text-sm text-muted-foreground">You do not have permission to access this page.</p>
                <Button className="mt-5" onClick={() => navigate({ to: '/' })}>Go to Home</Button>
            </Card>
        )
    }
    return <>{children}</>
}
const manageFormsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/forms',
    component: () => <ManagerOnly><FormsAdminPage /></ManagerOnly>,
})
const manageFormRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/forms/$id',
    component: function ManageForm() {
        const { id } = manageFormRoute.useParams()
        return <ManagerOnly><FormBuilderPage key={id} formId={id} /></ManagerOnly>
    },
})
const manageWorkflowsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/workflows',
    component: () => <ManagerOnly><WorkflowsAdminPage tab="workflows" /></ManagerOnly>,
})
const manageGroupsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/groups',
    component: () => <ManagerOnly><WorkflowsAdminPage tab="groups" /></ManagerOnly>,
})
const manageRoutingRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/manage/routing',
    component: () => <ManagerOnly><WorkflowsAdminPage tab="routing" /></ManagerOnly>,
})
const dashboardsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/dashboards', component: DashboardsPage })
const dashboardRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/dashboards/$id',
    component: function Dashboard() {
        const { id } = dashboardRoute.useParams()
        return <DashboardPage key={id} dashboardId={id} />
    },
})

const routeTree = rootRoute.addChildren([
    homeRoute,
    formsRoute,
    submissionsRoute,
    submissionRoute,
    submissionEditRoute,
    inboxRoute,
    manageFormsRoute,
    manageFormRoute,
    manageWorkflowsRoute,
    manageGroupsRoute,
    manageRoutingRoute,
    dashboardsRoute,
    dashboardRoute,
])

export const router = createRouter({
    routeTree,
    defaultErrorComponent: ({ error, reset }) => <ErrorScreen error={error} reset={reset} />,
    defaultNotFoundComponent: NotFoundScreen,
})

declare module '@tanstack/react-router' {
    interface Register {
        router: typeof router
    }
}
